import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import CancelOrderModal from '../../../components/CancelOrderModal';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [stats, setStats] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingOrderId, setAcceptingOrderId] = useState(null);
  const [decliningOrderId, setDecliningOrderId] = useState(null);
  const [markingDeliveredOrderId, setMarkingDeliveredOrderId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelOrderNumber, setCancelOrderNumber] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const restaurantMarkerRef = useRef(null);
  const deliveryMarkerRef = useRef(null);

  useEffect(() => {
    fetchStats();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  // Initialize map for active delivery
  useEffect(() => {
    if (googleMapsLoaded && stats?.activeDelivery && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    } else if (googleMapsLoaded && stats?.activeDelivery && mapInstanceRef.current) {
      updateMap();
    }
  }, [googleMapsLoaded, stats?.activeDelivery]);

  // Update map when user location changes
  useEffect(() => {
    if (mapInstanceRef.current && stats?.activeDelivery && user?.currentLocation?.coordinates) {
      updateMap();
    }
  }, [user?.currentLocation?.coordinates]);

  const fetchStats = async () => {
    try {
      const [statsRes, availableRes] = await Promise.all([
        axios.get(`${API_URL}/delivery-driver/dashboard/stats`),
        axios.get(`${API_URL}/delivery-driver/orders/available`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setAvailableOrders(Array.isArray(availableRes.data) ? availableRes.data : []);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOrder = async (orderId) => {
    setAcceptingOrderId(orderId);
    try {
      await axios.post(`${API_URL}/delivery-driver/orders/${orderId}/accept`);
      await fetchStats();
    } catch (error) {
      console.error('Error accepting order:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    } finally {
      setAcceptingOrderId(null);
    }
  };

  const handleAcceptDelivery = async (orderId) => {
    setAcceptingOrderId(orderId);
    try {
      await axios.post(`${API_URL}/delivery-driver/orders/${orderId}/accept-delivery`);
      await fetchStats();
    } catch (error) {
      console.error('Error accepting delivery:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    } finally {
      setAcceptingOrderId(null);
    }
  };

  const handleDeclineDelivery = async (orderId) => {
    setDecliningOrderId(orderId);
    try {
      await axios.post(`${API_URL}/delivery-driver/orders/${orderId}/decline-delivery`);
      await fetchStats();
    } catch (error) {
      console.error('Error declining delivery:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    } finally {
      setDecliningOrderId(null);
    }
  };

  const handleMarkAsDelivered = async (orderId) => {
    setMarkingDeliveredOrderId(orderId);
    try {
      await axios.patch(`${API_URL}/delivery-driver/orders/${orderId}/status`, { status: 'delivered' });
      await fetchStats();
    } catch (error) {
      console.error('Error marking order as delivered:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    } finally {
      setMarkingDeliveredOrderId(null);
    }
  };

  const handleCancelOrderClick = () => {
    if (!stats?.activeDelivery) return;
    setCancelOrderId(stats.activeDelivery._id);
    setCancelOrderNumber(stats.activeDelivery.orderNumber || stats.activeDelivery._id?.slice(-6));
    setShowCancelModal(true);
  };

  const handleCancelOrderConfirm = async (cancellationMessage) => {
    if (!cancelOrderId) return;
    try {
      await axios.patch(`${API_URL}/delivery-driver/orders/${cancelOrderId}/status`, {
        status: 'cancelled',
        cancellationMessage
      });
      setShowCancelModal(false);
      setCancelOrderId(null);
      setCancelOrderNumber(null);
      await fetchStats();
    } catch (error) {
      console.error('Error cancelling order:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    }
  };

  const initializeMap = () => {
    if (!window.google || !window.google.maps || !stats?.activeDelivery) return;

    const activeDelivery = stats.activeDelivery;
    const restaurant = activeDelivery.restaurant;
    const deliveryAddress = activeDelivery.deliveryAddress;

    if (!restaurant?.address?.coordinates || !deliveryAddress?.coordinates) return;

    // Center map between restaurant and delivery address
    const centerLat = (restaurant.address.coordinates.lat + deliveryAddress.coordinates.lat) / 2;
    const centerLng = (restaurant.address.coordinates.lng + deliveryAddress.coordinates.lng) / 2;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 13,
      center: { lat: centerLat, lng: centerLng },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    mapInstanceRef.current = map;

    // Add restaurant marker
    restaurantMarkerRef.current = new window.google.maps.Marker({
      position: {
        lat: restaurant.address.coordinates.lat,
        lng: restaurant.address.coordinates.lng
      },
      map: map,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
      },
      title: restaurant.name || t('admin.restaurant')
    });

    // Add delivery address marker
    deliveryMarkerRef.current = new window.google.maps.Marker({
      position: {
        lat: deliveryAddress.coordinates.lat,
        lng: deliveryAddress.coordinates.lng
      },
      map: map,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
      },
      title: t('admin.deliveryAddress')
    });

    // Add driver current location marker if available
    if (user?.currentLocation?.coordinates) {
      driverMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: user.currentLocation.coordinates.lat,
          lng: user.currentLocation.coordinates.lng
        },
        map: map,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
        },
        title: 'Your Location'
      });
    }

    // Draw route from restaurant to delivery address
    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map: map,
      preserveViewport: true,
      suppressMarkers: true // We're using custom markers
    });

    directionsRendererRef.current = directionsRenderer;

    directionsService.route(
      {
        origin: {
          lat: restaurant.address.coordinates.lat,
          lng: restaurant.address.coordinates.lng
        },
        destination: {
          lat: deliveryAddress.coordinates.lat,
          lng: deliveryAddress.coordinates.lng
        },
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === 'OK' && directionsRendererRef.current) {
          directionsRendererRef.current.setDirections(result);
        }
      }
    );

    // Fit bounds to show all markers
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({
      lat: restaurant.address.coordinates.lat,
      lng: restaurant.address.coordinates.lng
    });
    bounds.extend({
      lat: deliveryAddress.coordinates.lat,
      lng: deliveryAddress.coordinates.lng
    });
    if (user?.currentLocation?.coordinates) {
      bounds.extend({
        lat: user.currentLocation.coordinates.lat,
        lng: user.currentLocation.coordinates.lng
      });
    }
    map.fitBounds(bounds);
  };

  const updateMap = () => {
    if (!mapInstanceRef.current || !stats?.activeDelivery) return;

    // Update driver marker position if location is available
    if (user?.currentLocation?.coordinates) {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setPosition({
          lat: user.currentLocation.coordinates.lat,
          lng: user.currentLocation.coordinates.lng
        });
      } else {
        // Create driver marker if it doesn't exist
        driverMarkerRef.current = new window.google.maps.Marker({
          position: {
            lat: user.currentLocation.coordinates.lat,
            lng: user.currentLocation.coordinates.lng
          },
          map: mapInstanceRef.current,
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
          },
          title: t('admin.yourLocation')
        });
      }
    }
  };

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star filled"></i>);
    }
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt filled"></i>);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star"></i>);
    }
    return stars;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return <div className="loading-spinner">{t('admin.loadingDashboard')}</div>;
  }

  return (
    <div className="driver-dashboard">
      <h2>{t('admin.dashboardOverview')}</h2>
      
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{t('admin.totalOrders')}</h3>
          <p className="stat-number">{stats?.totalOrders || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.totalEarnings')}</h3>
          <p className="stat-number">{formatCurrency(stats?.totalEarnings || 0)}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.customerSatisfaction')}</h3>
          <div className="satisfaction-content">
            <div className="rating-stars">
              {stats?.customerSatisfaction?.averageRating > 0
                ? renderStars(stats.customerSatisfaction.averageRating)
                : <span className="no-rating">{t('admin.noRatingsYet')}</span>
              }
            </div>
            <p className="rating-text">
              {stats?.customerSatisfaction?.averageRating > 0
                ? `${stats.customerSatisfaction.averageRating.toFixed(1)} / 5.0`
                : 'N/A'
              }
            </p>
            <p className="rating-count">
              ({stats?.customerSatisfaction?.totalRatings || 0} {t('admin.reviews')})
            </p>
          </div>
        </div>
      </div>

      {/* Available orders (driver accepts first) */}
      {availableOrders.length > 0 && (
        <div className="pending-acceptance-section">
          <h3>{t('admin.availableOrders') || 'Available orders'}</h3>
          <p className="pending-acceptance-prompt">{t('admin.acceptOrderPrompt') || 'Accept an order to claim it. Restaurant will then confirm and prepare.'}</p>
          <div className="pending-acceptance-list">
            {availableOrders.map((order) => (
              <div key={order._id} className="pending-acceptance-card">
                <div className="pending-acceptance-info">
                  <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
                  <p><strong>{t('admin.customer')}:</strong> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}</p>
                  {(order.customerPhone || order.user?.phoneNumber) && (
                    <p><strong>{t('admin.phone')}:</strong> {order.customerPhone ?? order.user?.phoneNumber}</p>
                  )}
                  <p><strong>{t('admin.restaurant')}:</strong> {order.restaurant?.name || order.restaurantName}</p>
                  <p><strong>{t('admin.total')}:</strong> {formatCurrency(order.total)}</p>
                  {order.deliveryAddress && (
                    <p><strong>{t('admin.deliveryAddress')}:</strong> {order.deliveryAddress.street}, {order.deliveryAddress.city}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}</p>
                  )}
                </div>
                <div className="pending-acceptance-actions">
                  <button
                    type="button"
                    className="btn-accept-delivery"
                    onClick={() => handleAcceptOrder(order._id)}
                    disabled={acceptingOrderId === order._id}
                  >
                    {acceptingOrderId === order._id ? t('admin.updating') : (t('admin.acceptOrder') || 'Accept order')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waiting for restaurant (orders I claimed, pending restaurant accept) */}
      {stats?.pendingAcceptanceDeliveries && stats.pendingAcceptanceDeliveries.length > 0 && (
        <div className="pending-acceptance-section">
          <h3>{t('admin.waitingForRestaurant') || 'Waiting for restaurant'}</h3>
          <div className="pending-acceptance-list">
            {stats.pendingAcceptanceDeliveries.map((order) => (
              <div key={order._id} className="pending-acceptance-card">
                <div className="pending-acceptance-info">
                  <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
                  <p><strong>{t('admin.restaurant')}:</strong> {order.restaurant?.name || order.restaurantName}</p>
                  <p><strong>{t('admin.total')}:</strong> {formatCurrency(order.total)}</p>
                  <p className="pending-acceptance-prompt">{t('admin.waitingForRestaurantPrompt') || 'You have accepted. Waiting for restaurant to confirm.'}</p>
                </div>
                <div className="pending-acceptance-actions">
                  <button
                    type="button"
                    className="btn-decline-delivery"
                    onClick={() => handleDeclineDelivery(order._id)}
                    disabled={decliningOrderId === order._id}
                  >
                    {t('admin.declineDelivery')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Delivery Section */}
      {stats?.activeDelivery && (
        <div className="active-delivery-section">
          <h3>{t('admin.activeDelivery')}</h3>
          <div className="active-delivery-content">
            <div className="delivery-info">
              <div className="delivery-order-info">
                <h4>{t('admin.orderNumber')} {stats.activeDelivery.orderNumber || stats.activeDelivery._id.slice(-6)}</h4>
                <p><strong>{t('admin.customer')}:</strong> {stats.activeDelivery.user ? `${stats.activeDelivery.user.firstName || ''} ${stats.activeDelivery.user.lastName || ''}`.trim() : (stats.activeDelivery.customerName || '—')}</p>
                <p><strong>{t('admin.restaurant')}:</strong> {stats.activeDelivery.restaurant?.name || stats.activeDelivery.restaurantName}</p>
                <p><strong>{t('admin.total')}:</strong> {formatCurrency(stats.activeDelivery.total)}</p>
                {stats.activeDelivery.deliveryAddress && (
                  <p><strong>{t('admin.deliveryAddress')}:</strong> {stats.activeDelivery.deliveryAddress.street}, {stats.activeDelivery.deliveryAddress.city}{stats.activeDelivery.deliveryAddress.addressDetails ? ` — ${stats.activeDelivery.deliveryAddress.addressDetails}` : ''}</p>
                )}
                <p><strong>{t('admin.orderTime')}:</strong> {formatDate(stats.activeDelivery.createdAt)}</p>
                <div className="active-delivery-actions">
                  {stats.activeDelivery.status === 'readyForPickup' && (
                    <button
                      type="button"
                      className="btn-mark-delivered"
                      onClick={async () => {
                        setMarkingDeliveredOrderId(stats.activeDelivery._id);
                        try {
                          await axios.patch(`${API_URL}/delivery-driver/orders/${stats.activeDelivery._id}/status`, { status: 'outForDelivery' });
                          await fetchStats();
                        } catch (e) {
                          showError(e.response?.data?.message || t('admin.statusUpdateFailed'));
                        } finally {
                          setMarkingDeliveredOrderId(null);
                        }
                      }}
                      disabled={markingDeliveredOrderId === stats.activeDelivery._id}
                    >
                      {markingDeliveredOrderId === stats.activeDelivery._id ? t('admin.updating') : (t('admin.pickUpOrder') || 'Pick up order')}
                    </button>
                  )}
                  {stats.activeDelivery.status === 'outForDelivery' && (
                    <button
                      type="button"
                      className="btn-mark-delivered"
                      onClick={() => handleMarkAsDelivered(stats.activeDelivery._id)}
                      disabled={markingDeliveredOrderId === stats.activeDelivery._id}
                    >
                      {markingDeliveredOrderId === stats.activeDelivery._id ? t('admin.updating') : t('admin.markAsDelivered')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-cancel-delivery"
                    onClick={handleCancelOrderClick}
                  >
                    {t('admin.cancelOrder')}
                  </button>
                </div>
              </div>
            </div>
            <div className="delivery-map-container">
              <div ref={mapRef} className="delivery-map"></div>
            </div>
          </div>
        </div>
      )}

      {/* Top Orders Section */}
      <div className="top-orders-section">
        <h3>{t('admin.topOrders')}</h3>
        {stats?.topOrders && stats.topOrders.length > 0 ? (
          <div className="top-orders-list">
            {stats.topOrders.map((order, index) => (
              <div key={order._id} className="top-order-card">
                <div className="top-order-rank">#{index + 1}</div>
                <div className="top-order-details">
                  <div className="top-order-header">
                    <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
                    <span className="top-order-value">{formatCurrency(order.total)}</span>
                  </div>
                  <p className="top-order-customer">
                    <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                  </p>
                  <p className="top-order-restaurant">
                    <i className="fas fa-store"></i> {order.restaurant?.name || order.restaurantName}
                  </p>
                  <p className="top-order-date">
                    <i className="fas fa-clock"></i> {formatDate(order.createdAt)}
                  </p>
                  {(order.driverInstructions || order.customerInstructions) && (
                    <p className="top-order-driver-instructions">
                      <i className="fas fa-info-circle"></i> <strong>{t('admin.driverInstructions')}:</strong> {order.driverInstructions || order.customerInstructions}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-orders">
            <p>{t('admin.noCompletedOrders')}</p>
          </div>
        )}
      </div>

      <CancelOrderModal
        show={showCancelModal}
        orderNumber={cancelOrderNumber}
        onConfirm={handleCancelOrderConfirm}
        onClose={() => { setShowCancelModal(false); setCancelOrderId(null); setCancelOrderNumber(null); }}
      />
    </div>
  );
};

export default Dashboard;
